import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, DownloadCloud, RefreshCcw, Server, Unlink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import { formatDateTime } from '../utils/datetime';

const DEFAULT_CLOUD_URL = 'https://api.generic.nextwavesaas.tech';

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value}</p>
  </div>
);

/** Admin page of a venue LAN server: import an event from the cloud and watch two-way sync. */
const LanServerPage = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState({ cloudUrl: DEFAULT_CLOUD_URL, eventId: '', syncKey: '' });
  const [unlinkId, setUnlinkId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['lanStatus'],
    queryFn: () => ApiService.getLanStatus(),
    refetchInterval: 15000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['lanStatus'] });

  const importMutation = useMutation({
    mutationFn: () => ApiService.importLanEvent({
      cloudUrl: form.cloudUrl.trim(),
      eventId: form.eventId.trim(),
      syncKey: form.syncKey.trim(),
    }),
    onSuccess: (result) => {
      showSuccess(`Imported ${result.eventName || result.eventId}. Registrations are downloading in the background.`);
      setForm((prev) => ({ ...prev, eventId: '', syncKey: '' }));
      queryClient.invalidateQueries();
    },
    onError: (err) => showError(err.message || 'Import failed. Check the internet connection, event ID and key.'),
  });

  const syncMutation = useMutation({
    mutationFn: () => ApiService.syncLanNow(),
    onSuccess: refresh,
    onError: (err) => showError(err.message || 'Sync failed'),
  });

  const unlinkMutation = useMutation({
    mutationFn: (eventId) => ApiService.unlinkLanEvent(eventId),
    onSuccess: () => {
      setUnlinkId(null);
      refresh();
    },
    onError: (err) => showError(err.message || 'Could not unlink'),
  });

  if (!isLoading && !data?.lanMode) {
    return (
      <div className="p-2 sm:p-4 lg:p-6">
        <p className="text-sm text-gray-600">This server is not running in LAN mode.</p>
      </div>
    );
  }

  const links = data?.links || [];
  const canImport = form.cloudUrl.trim() && form.eventId.trim() && form.syncKey.trim();

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">LAN Server</h1>
        <p className="text-sm text-gray-600 mt-1">
          Desks on this network use this server. Changes sync with the cloud every minute whenever internet is available.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <DownloadCloud className="w-5 h-5" /> Import event from cloud
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            In the cloud admin, open the event settings and generate a sync key under "Venue LAN server".
            Importing again refreshes the event settings, badge layouts and scan purposes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="lan-cloud-url">Cloud URL</Label>
              <Input
                id="lan-cloud-url"
                value={form.cloudUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, cloudUrl: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lan-event-id">Event ID</Label>
              <Input
                id="lan-event-id"
                value={form.eventId}
                onChange={(e) => setForm((prev) => ({ ...prev, eventId: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lan-sync-key">Sync key</Label>
              <Input
                id="lan-sync-key"
                type="password"
                value={form.syncKey}
                onChange={(e) => setForm((prev) => ({ ...prev, syncKey: e.target.value }))}
              />
            </div>
          </div>
          <Button onClick={() => importMutation.mutate()} disabled={!canImport || importMutation.isPending}>
            {importMutation.isPending ? 'Importing…' : 'Import'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Server className="w-5 h-5" /> Synced events
          </CardTitle>
          {links.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
              <RefreshCcw className={`w-4 h-4 mr-1.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              Sync now
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {links.length === 0 && <p className="text-sm text-gray-500">No event imported yet.</p>}
          {links.map((link) => (
            <div key={link.eventId} className="rounded-lg border p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{link.eventName || link.eventId}</p>
                  <p className="text-xs text-gray-500 truncate">{link.eventId} · {link.cloudUrl}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setUnlinkId(link.eventId)} aria-label="Stop syncing">
                  <Unlink className="w-4 h-4 text-red-600" />
                </Button>
              </div>
              {link.lastError ? (
                <p className="flex items-start gap-1.5 text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  Cloud not reachable, will retry: {link.lastError}
                </p>
              ) : (
                <p className="flex items-center gap-1.5 text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4" /> In sync
                </p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Last sync" value={link.lastSyncAt ? formatDateTime(link.lastSyncAt) : '—'} />
                <Stat label="Waiting to upload" value={link.pendingPush ?? 0} />
                <Stat label="Uploaded" value={link.pushedTotal ?? 0} />
                <Stat label="Downloaded" value={link.pulledTotal ?? 0} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <ConfirmationDialog
        isOpen={!!unlinkId}
        onClose={() => setUnlinkId(null)}
        onConfirm={() => unlinkMutation.mutate(unlinkId)}
        title="Stop syncing this event?"
        message="Local data stays on this server, but changes are no longer sent to the cloud. Run any pending sync first."
        confirmText="Stop syncing"
        isLoading={unlinkMutation.isPending}
      />
    </div>
  );
};

export default LanServerPage;
